'use client';
import { BREAKPOINT_SP, zIndexMap } from '@/util/const';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

type Props = {
  handler: (setIsOpen: Function) => ReactNode;
  children: (setIsOpen: Function, refObj: any) => ReactNode;
  externalHandler?: boolean;
  pos: 'left' | 'center' | 'right';
  target: string;
};

const PullDownFrame: React.FC<Props> = (props) => {
  let root =
    typeof window !== 'undefined' && document.querySelector(props.target);

  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const refObj = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);
  useEffect(() => {
    if (props.externalHandler != undefined) {
      setIsOpen(props.externalHandler);
    }
  }, [props.externalHandler]);

  const el = refObj.current;
  const hundleClickOutside = useCallback(
    (e: MouseEvent) => {
      setIsOpen((o) => {
        if (o) {
          if (
            !el?.parentElement?.contains(e.target as Node) &&
            e.target != refObj.current?.parentElement?.parentElement
          ) {
            document.removeEventListener('click', hundleClickOutside);
            return false;
          }
        }
        return o;
      });
    },
    [refObj.current],
  );

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        document.addEventListener('click', hundleClickOutside);
      }, 400);
    }
    return () => {
      document.removeEventListener('click', hundleClickOutside);
    };
  }, [isOpen]);

  const height =
    refObj.current && refObj.current.getBoundingClientRect().height;
  const [containerPos, setContainerPos] = useState({
    bottom: 0,
    left: 0,
    right: 0,
  });
  useLayoutEffect(() => {
    function updateSize() {
      if (containerRef.current) {
        return setContainerPos(containerRef.current.getBoundingClientRect());
      } else {
        return setContainerPos({ bottom: 0, left: 0, right: 0 });
      }
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [containerRef.current]);
  // console.log(root, isOpen);
  return isMounted ? (
    <Container ref={containerRef}>
      {props.handler(setIsOpen)}
      {root &&
        ReactDOM.createPortal(
          <ModalContainer
            $isOpen={isOpen}
            height={height ? height : 0}
            $pos={props.pos}
            $posRect={containerPos}
            className="pulldown-container"
          >
            <div>{props.children(setIsOpen, refObj)}</div>
          </ModalContainer>,
          root!,
        )}
    </Container>
  ) : (
    props.handler(setIsOpen)
  );
};

export default PullDownFrame;

const Container = styled.div`
  position: relative;
  width: fit-content;
  height: fit-content;
`;

const ModalContainer = styled.div<{
  $isOpen: boolean;
  height: number;
  $pos: 'left' | 'center' | 'right';
  $posRect: DOMRect | { bottom: number; left: number; right: number };
}>`
  position: absolute;
  /* z-index: ${zIndexMap.header - 1}; */
  height: ${(props) => (props.$isOpen ? props.height : '0')}px;
  width: max-content;
  filter: drop-shadow(0 10px 10px rgb(0, 0, 0, 0.4));
  ${(props) => {
    switch (props.$pos) {
      case 'left':
        return (
          'left:' +
          props.$posRect.left +
          `px;  transform: translate(0, 0); top: ${props.$posRect.bottom}px;`
        );
      case 'center':
        return (
          'left:' +
          (props.$posRect.left +
            (props.$posRect.right - props.$posRect.left) / 2) +
          `px;  transform: translate(-50%, 0); top: ${props.$posRect.bottom}px;`
        );
      case 'right':
        return (
          'left:' +
          props.$posRect.right +
          'px;  transform: translate(-100%, 0); '
        );
    }
  }}
  @media (max-width: ${BREAKPOINT_SP}) {
    transition: left 0.5s;
    height: 100vh;
    width: 100vw;
    top: unset;
    bottom: 0;
    position: absolute;
    left: ${(props) => (props.$isOpen ? '0' : '-100vw')};
  }
  background-color: white;
  border-radius: 4px;
  opacity: ${(props) => (props.$isOpen ? 1 : 0.9)};
  transition: 0.4s height, 0.1s opacity;

  overflow: hidden;

  * {
    opacity: ${(props) => (props.$isOpen ? 1 : 0.9)};
    transition: 0.1s opacity;
  }
`;
